import React, { useMemo } from 'react'
import { 
  Calendar, Briefcase, Home, Users as UsersIcon,
  AlertCircle, CheckCircle, Clock
} from 'lucide-react'
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, 
  isSameMonth, isSameDay, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns'

interface KidEvent {
  id: string
  user_id: string
  title: string
  type: 'work' | 'julies' | 'friends' | 'event' | 'sick' | 'vacation'
  start_date: string
  end_date: string
  all_day: boolean
  requires_coverage: boolean
  coverage_status?: 'pending' | 'covered' | 'bidding'
  covered_by?: string
  notice_days?: number
  bonus_points?: number
  notes?: string
  created_at: string
  approved: boolean
  approved_by?: string
}

interface Kid {
  id: string
  username: string
  display_name: string
  color_theme: string
}

interface CalendarViewProps {
  events: KidEvent[]
  kids: Kid[]
  onEventClick?: (event: KidEvent) => void
  selectedKid?: string
}

const EVENT_TYPE_CONFIG = {
  work: { icon: Briefcase, color: 'bg-blue-500', textColor: 'text-blue-700' },
  julies: { icon: Home, color: 'bg-purple-500', textColor: 'text-purple-700' },
  friends: { icon: UsersIcon, color: 'bg-green-500', textColor: 'text-green-700' },
  event: { icon: Calendar, color: 'bg-orange-500', textColor: 'text-orange-700' },
  sick: { icon: AlertCircle, color: 'bg-red-500', textColor: 'text-red-700' },
  vacation: { icon: Calendar, color: 'bg-yellow-500', textColor: 'text-yellow-700' },
  holiday: { icon: Calendar, color: 'bg-indigo-500', textColor: 'text-indigo-700' },
  present: { icon: CheckCircle, color: 'bg-green-500', textColor: 'text-green-700' },
  unavailable: { icon: AlertCircle, color: 'bg-gray-500', textColor: 'text-gray-700' }
}

export const KidExceptionCalendarView: React.FC<CalendarViewProps> = ({ 
  events, 
  kids, 
  onEventClick,
  selectedKid 
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date())
  
  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth))
    const end = endOfWeek(endOfMonth(currentMonth))
    return eachDayOfInterval({ start, end })
  }, [currentMonth])

  const eventsByDate = useMemo(() => {
    const map = new Map<string, KidEvent[]>()
    
    events.forEach(event => {
      const start = parseISO(event.start_date)
      const end = parseISO(event.end_date)
      const days = eachDayOfInterval({ start, end })
      
      days.forEach(day => {
        const key = format(day, 'yyyy-MM-dd')
        if (!map.has(key)) {
          map.set(key, [])
        }
        map.get(key)!.push(event)
      })
    })
    
    return map
  }, [events])

  const getEventsForDay = (day: Date) => {
    const key = format(day, 'yyyy-MM-dd')
    return eventsByDate.get(key) || []
  }

  const renderEvent = (event: KidEvent, isMultiDay: boolean = false) => {
    const kid = kids.find(k => k.id === event.user_id)
    const typeConfig = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.unavailable
    const Icon = typeConfig.icon
    
    return (
      <div
        key={event.id}
        onClick={() => onEventClick?.(event)}
        className={`text-xs p-1 mb-1 rounded cursor-pointer hover:opacity-80 transition-opacity
          ${isMultiDay ? 'flex items-center' : ''} 
          ${typeConfig.color} bg-opacity-20 border-l-2 ${typeConfig.color.replace('bg-', 'border-')}`}
        title={`${event.title} - ${kid?.display_name}`}
      >
        <div className="flex items-center gap-1">
          {kid && (
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: kid.color_theme }}
            />
          )}
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className={`truncate font-medium ${typeConfig.textColor}`}>
            {event.title}
          </span>
          {event.requires_coverage && (
            <div className="ml-auto flex-shrink-0">
              {event.coverage_status === 'covered' ? (
                <CheckCircle className="w-3 h-3 text-green-600" />
              ) : event.coverage_status === 'bidding' ? (
                <Clock className="w-3 h-3 text-yellow-600" />
              ) : (
                <AlertCircle className="w-3 h-3 text-gray-500" />
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setCurrentMonth(new Date())}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-t-lg overflow-hidden">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 px-2 py-2 text-center text-xs font-medium text-gray-700">
              {day}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-b-lg overflow-hidden">
          {monthDays.map((day, dayIdx) => {
            const dayEvents = getEventsForDay(day)
            const isToday = isSameDay(day, new Date())
            const isCurrentMonth = isSameMonth(day, currentMonth)
            
            const filteredEvents = selectedKid 
              ? dayEvents.filter(e => e.user_id === selectedKid)
              : dayEvents

            return (
              <div
                key={dayIdx}
                className={`
                  bg-white p-2 min-h-[100px] relative
                  ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}
                  ${isToday ? 'bg-blue-50' : ''}
                `}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1 overflow-y-auto max-h-[80px]">
                  {filteredEvents.slice(0, 3).map(event => renderEvent(event))}
                  {filteredEvents.length > 3 && (
                    <div className="text-xs text-gray-500 font-medium pl-1">
                      +{filteredEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t bg-gray-50">
        <div className="flex flex-wrap gap-4 text-xs">
          {Object.entries(EVENT_TYPE_CONFIG).map(([type, config]) => (
            <div key={type} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded ${config.color}`} />
              <span className="capitalize text-gray-600">{type === 'julies' ? "Other Parent" : type}</span>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-600" />
              <span className="text-gray-600">Covered</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-yellow-600" />
              <span className="text-gray-600">Bidding</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-gray-500" />
              <span className="text-gray-600">Pending</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}